import React, { useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import { Plus, MapPin } from "lucide-react";

export default function LocationsManager({ meta, onChanged }) {
  const [expanded, setExpanded] = useState(false);
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);
  const customs = meta.custom_locations || [];

  const add = async () => {
    const name = newName.trim();
    if (!name) return;
    setBusy(true);
    try {
      await api.post("/inventory/locations", { name });
      toast.success(`Locația „${name}" a fost adăugată`);
      setNewName("");
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la adăugare");
    } finally { setBusy(false); }
  };

  const del = async (name) => {
    setBusy(true);
    try {
      await api.delete(`/inventory/locations?name=${encodeURIComponent(name)}`);
      toast.success(`Locația „${name}" a fost ștearsă`);
      onChanged && onChanged();
    } catch (e) {
      const msg = e?.response?.data?.detail || "";
      if (e?.response?.status === 409) {
        if (window.confirm(`${msg}\n\nForțezi ștergerea? (item-urile rămân, dar locația dispare)`)) {
          try {
            await api.delete(`/inventory/locations?name=${encodeURIComponent(name)}&force=true`);
            toast.success(`„${name}" ștearsă forțat`);
            onChanged && onChanged();
          } catch { toast.error("Eroare la ștergere forțată"); }
        }
      } else {
        toast.error(msg || "Eroare la ștergere");
      }
    } finally { setBusy(false); }
  };

  return (
    <div className="card-flat p-3" data-testid="locations-manager">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <MapPin size={16} className="text-[var(--primary)]"/>
          <span className="small-label">Locații custom</span>
          <span className="text-xs text-zinc-500">({customs.length})</span>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setExpanded((v) => !v)}
          data-testid="locations-manager-toggle"
        >
          {expanded ? "▾ Ascunde" : "▸ Gestionează locațiile"}
        </Button>
      </div>

      {expanded && (
        <div className="mt-3 space-y-3 border-t pt-3">
          <div className="flex items-center gap-2 flex-wrap">
            <Input
              placeholder="ex: GARAJ NOU, SET CASTANIU, ECHIPA Z..."
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
              className="h-9 max-w-[300px]"
              data-testid="locations-manager-input"
              disabled={busy}
            />
            <Button
              size="sm"
              onClick={add}
              disabled={busy || !newName.trim()}
              className="bg-[var(--primary)]"
              data-testid="locations-manager-add"
            >
              <Plus size={14} className="mr-1"/> Adaugă
            </Button>
          </div>

          {customs.length === 0 ? (
            <div className="text-xs text-zinc-400 italic">
              Nicio locație custom adăugată. Locațiile sistem ({meta.built_in_locations?.length || 0}) nu pot fi șterse.
            </div>
          ) : (
            <ul className="space-y-1">
              {customs.map((loc) => (
                <li key={loc} className="flex items-center justify-between text-sm px-2 py-1.5 rounded bg-zinc-50 border border-zinc-200" data-testid={`locations-manager-row-${loc}`}>
                  <span className="font-semibold">📍 {loc}</span>
                  <button
                    type="button"
                    onClick={() => del(loc)}
                    disabled={busy}
                    className="text-red-600 hover:text-red-800 text-xs px-2 py-0.5 hover:bg-red-50 rounded"
                    data-testid={`locations-manager-delete-${loc}`}
                  >
                    🗑 Șterge
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
